<template>
  <div class="shortcuts text-dark">
    <div class="shortcuts__buttons">
      <label class="marg-10-right">{{ $t('vue2.substitute_hours.shortcuts') }}:</label>
      <button class="do-bttn" @click="getAbsentUsers">{{ $t('vue2.substitute_hours.show_all_absent') }}</button>
      <DDropdown :isShowDots="false" :isBottomStart="true" :propOffset="[5, 5]" v-if="isShowExternalShortCuts">
        <button slot="title" class="do-bttn dropdown-button">Rapporter <span class="icon-arrow-down5"></span></button>
        <ul slot="list">
          <li
            class="li-links__link"
            v-for="schoolLink in schoolLinks"
            :key="schoolLink.link">
            <a :href="schoolLink.link" target="_blank">{{ schoolLink.title }}
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                   stroke-width="1.5" stroke="currentColor" class="tw-icon w-6 h-6">
                <path stroke-linecap="round" stroke-linejoin="round"
                    d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
              </svg>
            </a>
          </li>
        </ul>
      </DDropdown>
    </div>
    <div class="shortcuts__overlay__bg" v-if="isShowAbsentUsers" @click="close"></div>
    <transition name="slideUp">
      <div class="shortcuts__overlay text-dark" id="printList" ref="overlay" v-if="isShowAbsentUsers">
        <div class="shortcuts__overlay__header">
          <h2 class="text-dark"><span v-if="isLoading">{{ $t('vue2.substitute_hours.getting_all') }}</span><span
            v-else>{{ absentUsers.length }}</span>
            {{ $t('vue2.substitute_hours.absent_in_the_period') }} {{ startDateFormatted }} - {{ endDateFormatted }}
          </h2>
          <div class="shortcuts__overlay__header__buttons">
            <button @click="print" class="no-print"><span class="icon-printer3"></span></button>
            <div class="shortcuts__overlay__close icon__bg-circle no-print" @click="close">
              <span class="icon-cross2"></span>
            </div>
          </div>
        </div>
        <Loader v-if="isLoading"></Loader>
        <div class="shortcuts__users" v-else>
          <div class="filters__for__reasons no-print">
            <DoFiltersFilter
              :title="i18n['filter_reasons']"
              :tags="absenceEventsTypes"
              :selectedTags="selectedReasonsToFilter"
              @select="toggleFilter"
              @clear="selectedReasonsToFilter = []">
            </DoFiltersFilter>
          </div>
          <h4 v-if="absenceEvents.length < 1" class="italic">{{
              $t('vue2.substitute_hours.there_is_no_one_absent')
            }}</h4>
          <ul>
            <li class="shortcuts-user text-dark" v-for="absentUser in visibleAbsentUsers" :key="absentUser.userCalendarId">
              <div class="shortcuts-user__header">
                <div class="shortcuts-user__header__name">{{ absentUser.name }}</div>
                <div><label>{{ $t('vue2.substitute_hours.days_with_absence') }}:</label>
                  {{ absentUser.days.length }}
                </div>
              </div>
              <ul class="text-dark">
                <li v-for="day in absentUser.days" :key="day.date">
                  <ul class="events__list">
                    <li class="events__list__event" v-for="item in day.items" :key="item.key">
                      <div class="events__list__event__meta">{{ $filters.capitalize($filters.ddd(item.event.start)) }} {{ $filters.LL(item.event.start) }}
                        kl. {{ $filters.LT(item.event.start) }} -
                        <template v-if="item.isMultiDay">{{ $filters.capitalize($filters.ddd(item.event.end)) }} {{ $filters.LL(item.event.end) }} kl.</template>
                        {{ $filters.LT(item.event.end) }},
                        <b>{{ item.event.title }}</b>
                        <span v-if="item.serial" class="events__list__event__period">
                          ({{ i18n['period'] }}: {{ $filters.LL(item.serial.from_date) }} -
                          {{ item.serial.to_date ? $filters.LL(item.serial.to_date) : i18n['ongoing'] }})
                        </span>
                      </div>
                      <div class="events__list__event__note" v-if="item.hasNote">
                        <span class="icon-pencil"></span>
                        <div v-html="item.event.teacher_notes"></div>
                      </div>
                      <p>{{ item.duration }}</p>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
          <div ref="loadMoreSentinel" class="shortcuts__load-more no-print" v-show="hasMoreAbsentUsers">
            <Loader :isSmall="true"></Loader>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import docendo from '../../docendo'
import Loader from '../common/Loader'
import moment from 'moment'
import DoFiltersFilter from '../DoFilters/DoFiltersFilter.vue'
import DDropdown from '../common/DDropdown.vue'

const I18N = {
  da: {
    filter_reasons: 'Årsager',
    period: 'Periode',
    ongoing: 'løbende'
  },
  en: {
    filter_reasons: 'Reasons',
    period: 'Period',
    ongoing: 'ongoing'
  },
  nb: {
    filter_reasons: 'Årsaker',
    period: 'Periode',
    ongoing: 'løpende'
  }
}

const USERS_PAGE_SIZE = 15

export default {
  components: {DDropdown, DoFiltersFilter, Loader},
  props: {
    start: {
      type: Object,
      default: moment()
    },
    end: {
      type: Object,
      default: moment()
    }
  },
  data () {
    return {
      isShowAbsentUsers: false,
      absenceEvents: [],
      eventSerialsInHash: {},
      isLoading: false,
      selectedReasonsToFilter: [],
      visibleUsersCount: USERS_PAGE_SIZE,
      requestId: 0
    }
  },
  watch: {
    isLoading (isLoading) {
      if (!isLoading) {
        this.observeLoadMoreSentinel()
      }
    },
    selectedReasonsToFilter () {
      this.visibleUsersCount = USERS_PAGE_SIZE
      this.observeLoadMoreSentinel()
    }
  },
  beforeDestroy () {
    this.disconnectLoadMoreObserver()
    document.querySelector('body').classList.remove('no-scroll')
  },
  computed: {
    i18n () {
      return I18N[this.locale]
    },
    user () {
      return this.$store.state.user
    },
    isPermissionsActivatedForSchool () {
      return this.$store.getters.isPermissionsActivatedForSchool
    },
    userPermissions () {
      return this.$store.getters.userPermissions
    },
    userIsSchoolAdmin () {
      return this.userHasPermission('can_manage_school')
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    schoolCountry () {
      return this.$store.getters.schoolCountry
    },
    isShowExternalShortCuts () {
      return this.schoolCountry.toLowerCase() === 'dk' && this.userIsSchoolAdmin
    },
    schoolLinks () {
      return [
        {
          link: window.location.origin + '/app#/dashboard/' + this.schoolId,
          title: 'Antal vikartimer pr. klasse for skoleåret'
        },
        {
          link: window.location.origin + '/app#/dashboard/absence/' + this.schoolId,
          title: 'Personalets fraværstyper fordelt over måneder'
        },
        {
          link: window.location.origin + '/app#/dashboard/absence/subs/' + this.schoolId,
          title: 'Vikartimer ift. Fraværstyper'
        },
        {
          link: window.location.origin + '/app#/dashboard/coverage/' + this.schoolId,
          title: 'Vikardækning pr. skole, årgang, klasse og fag'
        },
        {
          link: window.location.origin + '/app#/dashboard/continuity/' + this.schoolId,
          title: 'Vikarkontinuitet pr. klasse og fag'
        },
        {
          link: window.location.origin + '/app#/dashboard/absence/duration/' + this.schoolId,
          title: 'Fraværets varighed'
        }
      ]
    },
    startDateFormatted () {
      return moment(this.start).locale(this.locale).format('DD. MMMM YYYY')
    },
    endDateFormatted () {
      return moment(this.end).locale(this.locale).format('DD. MMMM YYYY')
    },
    absenceEventsTypes () {
      const types = []
      this.absenceEvents.forEach(event => {
        const idx = types.findIndex(type => type === event.title)
        if (idx < 0) {
          types.push(event.title)
        }
      })
      return types
    },
    locale () {
      return this.$store.getters.locale
    },
    // $t is expensive in this vue-i18n version, so resolve once instead of per event
    durationLabels () {
      return {
        hour: this.$t('vue2.substitute_hours.hour'),
        hours: this.$t('vue2.substitute_hours.hours'),
        min: this.$t('vue2.substitute_hours.min')
      }
    },
    allUsersInCalIdHash () {
      return this.$store.getters.allUsersInCalIdHash
    },
    schoolUsers () {
      return this.$store.getters.usersSortedOnName
    },
    schoolUsersIds () {
      return this.schoolUsers.map(user => user.id)
    },
    schoolUserCalIds () {
      return this.schoolUsers.map(user => user.user_calendar_id)
    },
    absenceEventsFiltered () {
      if (!this.selectedReasonsToFilter.length) {
        return this.absenceEvents // Hvis intet er valgt, returnér alle events
      }
      return this.absenceEvents.filter(event => this.selectedReasonsToFilter.includes(event.title))
    },
    serialsByCalendarId () {
      const index = {}
      Object.values(this.eventSerialsInHash).forEach(serial => {
        index[serial.calendar_id] = index[serial.calendar_id] || []
        index[serial.calendar_id].push({
          serial,
          fromDate: moment(serial.from_date).format('YYYY-MM-DD'),
          toDate: serial.to_date ? moment(serial.to_date).format('YYYY-MM-DD') : null
        })
      })
      return index
    },
    absentUsers () {
      const eventsByCalId = new Map()
      this.absenceEventsFiltered.forEach(event => {
        if (!eventsByCalId.has(event.calendar_id)) {
          eventsByCalId.set(event.calendar_id, [])
        }
        eventsByCalId.get(event.calendar_id).push(event)
      })
      const users = []
      eventsByCalId.forEach((events, calId) => {
        users.push({
          userCalendarId: calId,
          name: this.getUserNameFromCalId(calId),
          days: this.groupEventsInDays(events)
        })
      })
      const collator = new Intl.Collator(this.locale)
      return users.sort((a, b) => collator.compare(a.name, b.name))
    },
    visibleAbsentUsers () {
      return this.absentUsers.slice(0, this.visibleUsersCount)
    },
    hasMoreAbsentUsers () {
      return this.visibleUsersCount < this.absentUsers.length
    }
  },
  methods: {
    userHasPermission (permission) {
      if (!this.user) {
        return false
      }
      if (this.isPermissionsActivatedForSchool) {
        return this.userPermissions.indexOf(permission) > -1 || this.user.admin
      } else {
        return true
      }
    },
    toggleFilter (filter) {
      const idx = this.selectedReasonsToFilter.indexOf(filter)
      if (idx < 0) {
        this.selectedReasonsToFilter.push(filter)
      } else {
        this.selectedReasonsToFilter.splice(idx, 1)
      }
    },
    print () {
      this.visibleUsersCount = Infinity
      this.$nextTick(this.printList)
    },
    printList () {
      // https://stackoverflow.com/questions/52343006/how-to-print-a-part-of-a-vue-component-without-losing-the-style
      const prtHtml = document.getElementById('printList').innerHTML
      // Get all stylesheets HTML
      let stylesHtml = ''
      for (const node of [...document.querySelectorAll('link[rel="stylesheet"], style')]) {
        stylesHtml += node.outerHTML
      }
      // Open the print window
      const WinPrint = window.open('', '', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0')

      WinPrint.document.write(`<!DOCTYPE html><html><head>${stylesHtml}</head><body>${prtHtml}</body></html>`)
      setTimeout(_ => {
        WinPrint.document.close()
        WinPrint.focus()
        WinPrint.print()
        WinPrint.close()
      }, 1000)
    },
    hasNoteWithoutHTML (myString) {
      return myString.replace(/<[^>]*>?/gm, '')
    },
    open () {
      let el = document.querySelector('body')
      if (!el.classList.contains('no-scroll')) {
        el.classList.add('no-scroll')
      }
    },
    close () {
      this.isShowAbsentUsers = false
      this.disconnectLoadMoreObserver()
      document.querySelector('body').classList.remove('no-scroll')
    },
    observeLoadMoreSentinel () {
      this.$nextTick(() => {
        this.disconnectLoadMoreObserver()
        const root = this.$refs.overlay
        const sentinel = this.$refs.loadMoreSentinel
        if (!window.IntersectionObserver) {
          this.visibleUsersCount = Infinity
          return
        }
        if (!root || !sentinel) {
          return
        }
        this.loadMoreObserver = new window.IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting) && this.hasMoreAbsentUsers) {
            this.visibleUsersCount += USERS_PAGE_SIZE
            // Re-observe so the callback fires again if the sentinel is still in view
            this.observeLoadMoreSentinel()
          }
        }, {root, rootMargin: '600px 0px'})
        this.loadMoreObserver.observe(sentinel)
      })
    },
    disconnectLoadMoreObserver () {
      if (this.loadMoreObserver) {
        this.loadMoreObserver.disconnect()
        this.loadMoreObserver = null
      }
    },
    getAbsentUsers () {
      this.open()
      this.isShowAbsentUsers = true
      this.absenceEvents = []
      this.visibleUsersCount = USERS_PAGE_SIZE
      this.isLoading = true
      const requestId = ++this.requestId
      docendo.calendarEvents2(this.schoolUserCalIds, this.start, this.end, ['absence'], data => {
        if (requestId !== this.requestId) {
          return
        }
        this.absenceEvents = Object.freeze(data.length > 0 ? JSON.parse(data) : [])
        this.isLoading = false
      }, failure => {
        if (requestId === this.requestId) {
          this.isLoading = false
        }
      })
      this.getEventSerials(requestId)
    },
    getEventSerials (requestId) {
      this.eventSerialsInHash = {}
      const collected = {}
      const day = moment(this.start).startOf('day')
      const lastDay = moment(this.end).startOf('day')
      let pending = 0
      let flushTimer = null
      const flush = () => {
        clearTimeout(flushTimer)
        flushTimer = null
        if (requestId === this.requestId) {
          this.eventSerialsInHash = Object.freeze({...collected})
        }
      }
      // Throttled so many per-day responses don't each trigger a full re-render
      const done = () => {
        pending--
        if (pending === 0) {
          flush()
        } else if (!flushTimer) {
          flushTimer = setTimeout(flush, 500)
        }
      }
      while (day.isSameOrBefore(lastDay)) {
        pending++
        docendo.schoolEventSerialsList(this.schoolId, day.format('YYYY-MM-DD'), data => {
          const serials = Array.isArray(data) ? data : (data && Array.isArray(data.data) ? data.data : [])
          serials.forEach(serial => {
            collected[serial.id] = serial
          })
          done()
        }, failure => {
          console.log('schoolEventSerialsList failure', failure)
          done()
        })
        day.add(1, 'day')
      }
    },
    getEventSerial (event, eventDate) {
      const candidates = this.serialsByCalendarId[event.calendar_id] || []
      const match = candidates.find(({serial, fromDate, toDate}) => {
        const isSameReason = serial.subject_id ? serial.subject_id === event.subject_id : serial.subject_name === event.title
        return isSameReason && fromDate <= eventDate && (!toDate || toDate >= eventDate)
      })
      return match ? match.serial : false
    },
    groupEventsInDays (events) {
      const items = events.map((event, index) => {
        const start = moment(event.start)
        const date = start.format('YYYY-MM-DD')
        return {
          key: event.id || `${event.start}-${index}`,
          event,
          date,
          startMs: start.valueOf(),
          isMultiDay: !this.isSameDay(event.start, event.end),
          serial: this.getEventSerial(event, date),
          hasNote: !!event.teacher_notes && this.hasNoteWithoutHTML(event.teacher_notes).length > 0,
          duration: this.getDuration(event)
        }
      }).sort((a, b) => a.startMs - b.startMs)
      const days = []
      items.forEach(item => {
        const lastDay = days[days.length - 1]
        if (lastDay && lastDay.date === item.date) {
          lastDay.items.push(item)
        } else {
          days.push({date: item.date, items: [item]})
        }
      })
      return days
    },
    getUserNameFromCalId (calId) {
      return this.allUsersInCalIdHash[calId] && this.allUsersInCalIdHash[calId].name ? this.allUsersInCalIdHash[calId].name : 'User can not be found'
    },
    isSameDay (start, end) {
      return moment(start).isSame(moment(end), 'day')
    },
    getDuration (event) {
      return this.humanizeMinutes(event.duration)
    },
    humanizeMinutes (data) {
      const labels = this.durationLabels
      const minutes = data % 60
      const hours = (data - minutes) / 60
      const multiple = hours === 1 ? labels.hour : labels.hours
      return hours + ' ' + multiple + ' ' + minutes + ' ' + labels.min
    }
  }
}
</script>

<style>
.shortcuts__overlay {
  position: fixed;
  transition: transform .4s, -webkit-transform .4s;
  bottom: 0;
  left: 0;
  right: 0;
  height: 80vh;
  background-color: #edf0f5;
  overflow-y: auto;
  z-index: 9999999;
  box-shadow: 0 10px 40px -10px rgba(0, 64, 128, .2);
}

.shortcuts__overlay__bg {
  position: fixed;
  left: 0;
  bottom: 0;
  top: 0;
  right: 0;
  background: rgba(0, 0, 0, .1);
  z-index: 99999;
}

.shortcuts__overlay__header {
  display: flex;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #d8d8d8;
}

.shortcuts__overlay__header__buttons {
  display: flex;
  align-items: center;
}

.shortcuts__overlay__close {
  font-size: 20px !important;
  margin-left: 4rem;
}

.slideUp-enter-active {
  transition: transform .3s;
}

.slideUp-enter, .slideUp-leave-to {
  transform: translateY(100%);
}

.shortcuts__users {
  padding: 20px;
}

.shortcuts-user {
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .07);
  background: #ffffff;
  margin-bottom: 20px;
  border: 1px solid #d8d8d8;
}

.shortcuts-user .shortcuts-user__header {
  border-bottom: 1px solid #d8d8d8;
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
}

.shortcuts-user .shortcuts-user__header__name {
  font-size: 1.3rem;
  font-weight: bold;
}

.events__list > li {
  padding: 5px 20px;
  background-color: #fff;
  display: flex;
  justify-content: space-between;
}

.events__list > li:nth-child(odd) {
  background-color: #f8fafb;
}

.events__list__event > * {
  flex: 1;
}

.events__list__event p {
  text-align: right;
}

.events__list__event__note {
  display: flex;
  align-items: center;
}

.events__list__event__note > span {
  margin-right: 5px;
}

.events__list__event__note div {
  display: flex;
  align-items: center;
}

.shortcuts__load-more .loader {
  margin: 20px auto;
}

.filters__for__reasons {
  margin-bottom: 1rem;
}

.shortcuts__buttons {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.li-links__link a {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #222 !important;
  gap: 2rem;
}

.dropdown-button {
  font-size: 12px !important;
}

</style>



// WEBPACK FOOTER //
// HoursShowShortcuts.vue?1ac13a84